// Bluetooth Service for Ticket Window Interface
// Handles Web Bluetooth API interactions with Ticket Window devices

import { getDeviceById, BLUETOOTH_STATUS } from '../config/devices';

class BluetoothService {
    constructor() {
        this.device = null;
        this.server = null;
        this.service = null;
        this.txCharacteristic = null;
        this.rxCharacteristic = null;
        this.status = BLUETOOTH_STATUS.DISCONNECTED;
        this.onStatusChange = null;
        this.onDataReceived = null;
        this.onConnectionLost = null; // Callback for connection loss navigation
        this.selectedDeviceConfig = null;
        this.heartbeatInterval = null;
        this.lastHeartbeatResponse = null;
        this.heartbeatTimeout = null;
        this.consecutiveHeartbeatFailures = 0;
    }

    // Set status and notify listeners
    setStatus(newStatus) {
        this.status = newStatus;
        if (this.onStatusChange) {
            this.onStatusChange(newStatus);
        }
    }

    // Connect to a specific device by ID
    async connect(deviceId) {
        try {
            this.selectedDeviceConfig = getDeviceById(deviceId);
            if (!this.selectedDeviceConfig) {
                throw new Error(`Device with ID ${deviceId} not found`);
            }

            this.setStatus(BLUETOOTH_STATUS.CONNECTING);

            // Request device using namePrefix (same as working bluetooth-text-sender)
            this.device = await navigator.bluetooth.requestDevice({
                filters: [
                    { namePrefix: 'TicketWindow' },
                    { namePrefix: 'Ticket_Window' },
                    { namePrefix: 'InkyFrame' },
                    { namePrefix: 'Pico' }
                ],
                optionalServices: [this.selectedDeviceConfig.bluetooth.serviceUuid]
            });

            // Verify we got the right device
            if (!this.device.name || !this.device.name.includes('TicketWindow')) {
                throw new Error(`Selected device "${this.device.name}" is not a TicketWindow device`);
            }

            console.log(`Selected device: ${this.device.name}`);

            // Add disconnect listener
            this.device.addEventListener('gattserverdisconnected', () => {
                this.handleDisconnect();
            });

            // Connect to GATT server
            this.server = await this.device.gatt.connect();

            // Get the Nordic UART service
            this.service = await this.server.getPrimaryService(this.selectedDeviceConfig.bluetooth.serviceUuid);

            // Get characteristics
            this.txCharacteristic = await this.service.getCharacteristic(this.selectedDeviceConfig.bluetooth.txCharUuid);
            this.rxCharacteristic = await this.service.getCharacteristic(this.selectedDeviceConfig.bluetooth.rxCharUuid);

            // Start notifications on TX characteristic (device to client)
            await this.txCharacteristic.startNotifications();
            this.txCharacteristic.addEventListener('characteristicvaluechanged', (event) => {
                this.handleDataReceived(event.target.value);
            });

            this.setStatus(BLUETOOTH_STATUS.CONNECTED);
            console.log(`Connected to device: ${this.selectedDeviceConfig.name}`);

            // Send connection ready signal to device
            await this.sendConnectionReadySignal();

            // Start heartbeat monitoring
            this.startHeartbeat();

            return true;
        } catch (error) {
            console.error('Bluetooth connection failed:', error);
            this.setStatus(BLUETOOTH_STATUS.ERROR);
            throw error;
        }
    }

    // Disconnect from device
    async disconnect() {
        try {
            if (this.device && this.device.gatt.connected) {
                await this.device.gatt.disconnect();
            }
        } catch (error) {
            console.error('Error during disconnect:', error);
        }
        this.handleDisconnect();
    }

    // Check if an error indicates a connection loss
    isConnectionError(error) {
        const connectionErrorMessages = [
            'GATT Server is disconnected',
            'GATT operation failed',
            'Device is no longer connected',
            'Connection failed',
            'NetworkError',
            'Not connected'
        ];
        
        const errorMessage = error.message || error.toString();
        return connectionErrorMessages.some(msg => 
            errorMessage.toLowerCase().includes(msg.toLowerCase())
        );
    }

    // Handle disconnect event
    handleDisconnect() {
        console.log('Device disconnected - cleaning up connection');
        
        // Stop heartbeat monitoring
        this.stopHeartbeat();
        
        // Clean up connection state
        this.device = null;
        this.server = null;
        this.service = null;
        this.txCharacteristic = null;
        this.rxCharacteristic = null;
        this.selectedDeviceConfig = null;
        this.lastHeartbeatResponse = null;
        this.consecutiveHeartbeatFailures = 0;
        
        this.setStatus(BLUETOOTH_STATUS.DISCONNECTED);
        
        // Trigger navigation back to device selection if callback is set
        if (this.onConnectionLost) {
            console.log('Triggering automatic navigation to device selection screen');
            this.onConnectionLost();
        }
    }

    // Send data to device
    async sendData(data) {
        if (!this.rxCharacteristic) {
            throw new Error('Not connected to device');
        }

        try {
            const encoder = new TextEncoder();
            const encodedData = encoder.encode(data);
            
            // Check message size limit
            if (encodedData.length > this.selectedDeviceConfig.bluetooth.maxMessageSize) {
                throw new Error(`Message too large: ${encodedData.length} > ${this.selectedDeviceConfig.bluetooth.maxMessageSize} bytes`);
            }

            await this.rxCharacteristic.writeValue(encodedData);
            console.log('Data sent to device:', data);
        } catch (error) {
            console.error('Failed to send data:', error);
            
            // Check if error indicates connection loss
            if (this.isConnectionError(error)) {
                console.warn('Send data failed due to connection loss - triggering disconnect handling');
                this.handleDisconnect();
            }
            
            throw error;
        }
    }

    // Handle received data
    handleDataReceived(value) {
        const decoder = new TextDecoder();
        const receivedData = decoder.decode(value);
        
        console.log(`Data received from device (${receivedData.length} bytes):`, receivedData);
        
        // Check for heartbeat response
        if (receivedData.includes('OK: Heartbeat')) {
            this.handleHeartbeatResponse();
        }
        
        // Check for other responses
        if (receivedData.includes('OK: Connection acknowledged')) {
            console.log('Connection acknowledged by device');
        }
        
        if (receivedData.includes('OK: Reverted to default')) {
            console.log('Device reverted to default screen');
        }
        
        // Legacy callback for other data handlers
        if (this.onDataReceived) {
            this.onDataReceived(receivedData);
        }
    }

    // Send journey data to device (matches bluetooth-text-sender format)
    async sendJourneyData(journeyData) {
        if (!this.rxCharacteristic) {
            throw new Error('Not connected to device');
        }

        try {
            // Convert to JSON and then to bytes (same as sample app)
            const journeyJson = JSON.stringify(journeyData);
            const encoder = new TextEncoder();
            const data = encoder.encode(journeyJson);
            
            console.log(`Sending journey data (${data.length} bytes):`, journeyJson);
            
            // Send data in smaller chunks with delays for reliability (same as sample app)
            const chunkSize = 20;  // Match sample app chunk size
            let offset = 0;
            
            while (offset < data.length) {
                const chunk = data.slice(offset, offset + chunkSize);
                await this.rxCharacteristic.writeValue(chunk);
                offset += chunkSize;
                
                // Add delay between chunks for reliability (match sample app: 200ms)
                if (offset < data.length) {
                    await new Promise(resolve => setTimeout(resolve, 200));
                }
            }
            
            // Brief wait for device acknowledgment (match sample app)
            await new Promise(resolve => setTimeout(resolve, 100));
            
            console.log('Journey data sent successfully');
            
            // Reset heartbeat timer - journey data transmission may cause screen update
            this.resetHeartbeat();
        } catch (error) {
            console.error('Failed to send journey data:', error);
            throw error;
        } finally {
            this.isSending = false;
        }
    }

    // Send connection ready signal to device after successful connection
    async sendConnectionReadySignal() {
        if (!this.rxCharacteristic) {
            throw new Error('Not connected to device');
        }

        try {
            const readyMessage = JSON.stringify({ action: "connection_ready" });
            console.log('Sending connection ready signal to device...');
            
            // Use chunked transmission like journey data to ensure reliable delivery
            const encoder = new TextEncoder();
            const data = encoder.encode(readyMessage);
            const chunkSize = 20;
            let offset = 0;
            
            while (offset < data.length) {
                const chunk = data.slice(offset, offset + chunkSize);
                await this.rxCharacteristic.writeValue(chunk);
                offset += chunkSize;
                
                // Small delay between chunks
                if (offset < data.length) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            }
            
            // Reset heartbeat timer if monitoring is active - connection ready may cause screen update
            this.resetHeartbeat();
        } catch (error) {
            console.error('Failed to send connection ready signal:', error);
            throw error;
        }
    }


    // Get current connection status
    getStatus() {
        return this.status;
    }

    // Get connected device info
    getConnectedDevice() {
        return this.selectedDeviceConfig;
    }

    // Check if connected
    isConnected() {
        return this.status === BLUETOOTH_STATUS.CONNECTED;
    }

    // Start heartbeat monitoring (60-second intervals)
    startHeartbeat() {
        this.stopHeartbeat(); // Clear any existing heartbeat
        
        this.heartbeatInterval = setInterval(async () => {
            try {
                await this.sendHeartbeat();
            } catch (error) {
                console.error('Heartbeat failed:', error);
                this.handleHeartbeatFailure().catch(err => 
                    console.error('Error handling heartbeat failure:', err)
                );
            }
        }, 60000); // 60 seconds
        

    }

    // Stop heartbeat monitoring
    stopHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
        
        if (this.heartbeatTimeout) {
            clearTimeout(this.heartbeatTimeout);
            this.heartbeatTimeout = null;
        }
    }

    // Reset heartbeat timer (call when sending data that might cause screen updates)
    resetHeartbeat() {
        if (!this.isConnected() || !this.heartbeatInterval) {
            return; // No heartbeat monitoring active
        }

        // Clear any existing heartbeat timeout
        if (this.heartbeatTimeout) {
            clearTimeout(this.heartbeatTimeout);
            this.heartbeatTimeout = null;
        }
        
        // Reset consecutive failures (device was responsive enough to receive data)
        if (this.consecutiveHeartbeatFailures > 0) {
            this.consecutiveHeartbeatFailures = 0;
        }
        
        // Restart the heartbeat interval (next heartbeat in 60 seconds)
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = setInterval(async () => {
            try {
                await this.sendHeartbeat();
            } catch (error) {
                console.error('Heartbeat failed:', error);
                this.handleHeartbeatFailure().catch(err => 
                    console.error('Error handling heartbeat failure:', err)
                );
            }
        }, 60000); // 60 seconds
        

    }

    // Send heartbeat ping to device
    async sendHeartbeat() {
        if (!this.isConnected()) {
            throw new Error('Not connected to device');
        }

        const timestamp = new Date().toISOString();
        
        const heartbeatMessage = JSON.stringify({ 
            action: "heartbeat", 
            timestamp: timestamp 
        });
        
        // Clear any existing timeout first
        if (this.heartbeatTimeout) {
            clearTimeout(this.heartbeatTimeout);
        }
        
        // Set timeout BEFORE sending data to avoid race condition
        this.lastHeartbeatResponse = null;
        this.heartbeatTimeout = setTimeout(() => {
            console.warn('⚠️ HEARTBEAT TIMEOUT - no response received within 30 seconds');
            this.handleHeartbeatFailure().catch(err => 
                console.error('Error handling heartbeat failure:', err)
            );
        }, 30000);
        

        
        console.log('Sending heartbeat ping to device...');
        
        // Use chunked transmission for reliability
        const encoder = new TextEncoder();
        const data = encoder.encode(heartbeatMessage);
        const chunkSize = 20;
        let offset = 0;
        
        while (offset < data.length) {
            const chunk = data.slice(offset, offset + chunkSize);
            await this.rxCharacteristic.writeValue(chunk);
            offset += chunkSize;
            
            if (offset < data.length) {
                await new Promise(resolve => setTimeout(resolve, 50));
            }
        }

    }

    // Handle heartbeat failure (device offline or not responding)
    async handleHeartbeatFailure() {
        this.consecutiveHeartbeatFailures++;
        
        console.warn(`Heartbeat failure #${this.consecutiveHeartbeatFailures} - device may be busy (screen refreshing)`);
        
        if (this.consecutiveHeartbeatFailures >= 2) {
            console.error(`Multiple consecutive heartbeat failures - device appears offline, disconnecting`);
            
            // Stop heartbeat monitoring
            this.stopHeartbeat();
            
            // Perform clean disconnect (same as manual disconnect)
            // This will attempt to disconnect GATT connection and notify device
            await this.disconnect();
        } else {
            console.warn(`Waiting for ${2 - this.consecutiveHeartbeatFailures} more failure(s) before disconnecting`);
            // Continue heartbeat monitoring - next heartbeat will be sent in 60 seconds
        }
    }

    // Handle heartbeat response from device
    handleHeartbeatResponse() {
        this.lastHeartbeatResponse = new Date().toISOString();
        
        // Reset consecutive failure counter on successful response
        this.consecutiveHeartbeatFailures = 0;
        
        // Clear the heartbeat timeout
        if (this.heartbeatTimeout) {
            clearTimeout(this.heartbeatTimeout);
            this.heartbeatTimeout = null;
        } else {
            console.warn('WARNING: No heartbeat timeout was active when response received!');
        }
    }
}

// Create singleton instance
const bluetoothService = new BluetoothService();

export default bluetoothService;
